import { useEffect, useState, useCallback } from 'react'
import {
	useRecoilCallback,
	useRecoilState,
	useRecoilValue,
	useRecoilValueLoadable,
	useSetRecoilState,
	waitForAll,
	waitForAny,
} from 'recoil'
import {
	taskQueueState,
	lastFetchUpdate,
	fetchState,
	openedTask,
	lastFetchInboxUpdate,
	expandedInboxState,
	forceInboxUpdateState,
	inboxHasNewDataState,
	inboxFiltersState,
	taskNote,
} from './atoms'
import { fetchInbox, fetchTasks } from './selectors'

import { useHistory } from 'react-router-dom'
import { useMemo } from 'react'
import { useDeepCompareEffect } from 'react-use'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { API_URL, fetchOptions } from './fetchUtils'
import { Task } from 'models'
import { useAnalytics } from 'hooks/useAnalytics'
import useSession from 'hooks/useSession'
import AmplitudeTaskParameters from 'models/Amplitude/AmplitudeTaskParameters'

const DEFAULT_CACHE_TIME = 60000 * 5 // 5 minutes

export const useReload = () => {
	const updateTimestamps = useRecoilCallback(({ set }) => (paths = []) => {
		paths.forEach((path) => {
			set(lastFetchUpdate(path), Date.now())
		})
	})
	return updateTimestamps
}

export const useIntervalPooling = (
	paths,
	interval = DEFAULT_CACHE_TIME,
	pause
) => {
	const updateTimestamps = useRecoilCallback(({ set }) => (paths = []) => {
		paths.forEach((path) => {
			set(lastFetchUpdate(path), Date.now())
		})
	})

	useEffect(() => {
		if (pause) return
		let timeout
		const doUpdateTimestamps = (update) => {
			if (update) {
				updateTimestamps(paths)
			}
			timeout = setTimeout(doUpdateTimestamps, interval, true)
		}
		doUpdateTimestamps()
		return () => {
			clearTimeout(timeout)
		}
	}, [updateTimestamps, interval, paths, pause])
}

export const useLoaded = () => {
	const [loaded, setLoaded] = useState(false)
	const [contentsReady, setContentsReady] = useState(false)
	useEffect(() => {
		if (contentsReady) {
			setLoaded(true)
		}
	}, [contentsReady])
	return [loaded, setContentsReady]
}

export const useFetchList = (
	paths,
	maps,
	cacheTime = DEFAULT_CACHE_TIME,
	useNoWait,
	ignoreAction,
	ignoreAutoReload
) => {
	const waitFunc = useNoWait ? waitForAny : waitForAll

	const updateTimestamps = useRecoilCallback(({ set }) => (paths) => {
		paths.forEach((path) => {
			set(lastFetchUpdate(path), Date.now())
		})
	})

	let loadableState = useRecoilValueLoadable(
		waitFunc(paths.map((path) => fetchState(path)))
	)

	const pathsTimestamps = useRecoilValue(
		waitForAll(paths.map((path) => lastFetchUpdate(path)))
	)

	const { action } = useHistory()
	const now = Date.now()
	const expiredPaths =
		ignoreAction || action !== 'POP'
			? paths.filter((_, index) => {
					const lastTimestamp = pathsTimestamps[index]
					const delta = now - lastTimestamp
					const isLoading =
						loadableState.state === 'loading' ||
						(loadableState.contents[index] &&
							loadableState.contents[index].state === 'loading')
					return !isLoading && cacheTime >= 0 && delta > cacheTime
			  })
			: []

	if (!ignoreAutoReload && expiredPaths.length) {
		updateTimestamps(expiredPaths)
	}

	if (maps && maps.length) {
		if (!useNoWait) {
			if (loadableState.state === 'hasValue') {
				loadableState = loadableState.map((resultList) => {
					if (!resultList) return null
					return resultList.map((response, index) => {
						const map = maps[index]
						if (!map) return response
						return Array.isArray(response)
							? response.map((json, itemIndex) => {
									return json ? map(json, itemIndex) : null
							  })
							: response
							? map(response, 0)
							: null
					})
				})
			}
		} else {
			loadableState = loadableState.map((layers) => {
				return layers.map
					? layers.map((layerLoadable, index) => {
							return layerLoadable.map((response) => {
								const map = maps[index]
								if (!map) return response
								return Array.isArray(response)
									? response.map((json, itemIndex) => {
											return json
												? map(json, itemIndex)
												: null
									  })
									: response
									? map(response, 0)
									: null
							})
					  })
					: layers
			})
		}
	}
	const { state, contents } = loadableState
	if (state === 'hasError') {
		throw loadableState.contents
	}
	return {
		state,
		contents: Array.isArray(contents) ? contents : [],
		timestamps: pathsTimestamps,
		reload: () => {
			updateTimestamps(paths)
		},
	}
}

export const useNoWaitFetchList = (
	paths,
	maps,
	cacheTime = DEFAULT_CACHE_TIME
) => {
	return useFetchList(paths, maps, cacheTime, true)
}

export const useFetch = (
	path,
	map,
	cacheTime = DEFAULT_CACHE_TIME,
	ignoreAction
) => {
	path = path || '*'
	cacheTime = path ? cacheTime : 0
	const { action } = useHistory()
	const lastTimestamp = useRecoilValue(lastFetchUpdate(path))
	const delta = Date.now() - lastTimestamp
	// const reset = useResetRecoilState(fetchState(path))
	const reset = useRecoilCallback(({ set }) => (paths = []) => {
		paths.forEach((path) => {
			set(lastFetchUpdate(path), Date.now())
		})
	})
	let loadableState = useRecoilValueLoadable(fetchState(path))

	if (
		cacheTime >= 0 &&
		(ignoreAction || action !== 'POP') &&
		delta > cacheTime
	) {
		reset([path])
	}
	if (map) {
		loadableState = loadableState.map((response) => {
			if (!response) return null
			return Array.isArray(response)
				? response.map((json, itemIndex) => {
						return map(json, itemIndex)
				  })
				: map(response, 0)
		})
	}
	if (loadableState.state === 'hasError') {
		throw loadableState.contents
	}
	return loadableState
}

export const useQueueTask = () => {
	const setTasksQueue = useSetRecoilState(taskQueueState)
	const queue = (task, rect) => {
		setTasksQueue((queue) => {
			const currentQueue = [...queue] //TODO Reorder queue
			const taskIndex = currentQueue.findIndex((t) => t.id === task.id)
			const activeTask = currentQueue.find((t) => t.metadata.$active)
			let nextQueue = currentQueue.map((t) => ({
				...t,
				metadata: {
					...(t.metadata || {}),
					origin: null,
					$new: false,
					$active: false,
					$prevActive: activeTask,
				},
			}))
			if (taskIndex >= 0) {
				nextQueue.splice(taskIndex, 1, {
					...task,
					metadata: {
						...(task.metadata || {}),
						origin: rect,
						$new: false,
						$active: true,
						$prevActive: activeTask,
					},
				})
			} else {
				nextQueue.unshift({
					...task,
					metadata: {
						...(task.metadata || {}),
						origin: rect,
						$new: true,
						$active: true,
						$prevActive: activeTask,
					},
				})
				nextQueue = nextQueue.slice(0, 1)
			}
			return nextQueue
		})
	}

	const unQueue = (task) => {
		setTasksQueue((queue) => {
			const currentQueue = [...queue]
			const taskIndex = currentQueue.findIndex((t) => t.id === task.id)
			currentQueue.splice(taskIndex, 1)
			return currentQueue
		})
	}

	const minimize = (task) => {
		setTasksQueue((queue) => {
			const nextQueue = [...queue]
			const taskIndex = nextQueue.findIndex((t) => t.id === task.id)
			nextQueue.splice(taskIndex, 1, {
				...task,
				metadata: {
					...task.metadata,
					$active: false,
				},
			})
			return nextQueue
		})
	}

	return [queue, unQueue, minimize]
}

export const useTaskManager = () => {
	const { i18n, pushNotification } = useGlobal()
	const fetchedTasks = useRecoilValueLoadable(fetchTasks)
	const history = useHistory()
	const setOpenTask = useSetRecoilState(openedTask)
	const setNoteValue = useSetRecoilState(taskNote)
	const currentOpenedTask = useRecoilValue(openedTask)
	const reload = useReload()
	const { dispatchAnalyticsEvent } = useAnalytics()
	const { user } = useSession()

	const nextTasksUrl = `${API_URL}/api/tasks/next`

	const tasks = useMemo(
		() => (fetchedTasks.state === 'hasValue' ? fetchedTasks.contents : []),
		[fetchedTasks.contents, fetchedTasks.state]
	)

	const openTask = useCallback(
		(taskId) => {
			const selectedTask = tasks.find((task) => task.id === taskId)
			setNoteValue(selectedTask?.note || '')
			setOpenTask((prev) => {
				const nextTask =
					prev?.openTask?.id !== taskId
						? { ...prev, openTask: selectedTask }
						: prev

				return nextTask
			})

			dispatchAnalyticsEvent(
				'openTask',
				AmplitudeTaskParameters.fromJSON(selectedTask)
			)
		},

		[dispatchAnalyticsEvent, setNoteValue, setOpenTask, tasks]
	)

	const openNextTask = async (processId) => {
		const fetchedNextTask = await fetch(
			processId ? `${nextTasksUrl}?processId=${processId}` : nextTasksUrl,
			fetchOptions({})
		).then((r) => r.json())

		const nextTask = Task.fromJSON(fetchedNextTask)

		if (nextTask.id) {
			history.push(
				`/clinical-processes/${nextTask?.processId}/appointments?taskId=${nextTask.id}`
			)

			setOpenTask((prev) => {
				const nextTaskState =
					prev?.openTask?.id !== nextTask.id
						? { ...prev, openTask: nextTask }
						: prev

				return nextTaskState
			})
		} else {
			pushNotification(
				undefined,
				'warning',
				i18n`There are no more tasks!`
			)
		}

		reload([
			'/tasks/me?state=Created',
			'/tasks/me?state=Postponed',
			'/tasks/stats',
		])
	}

	const openSingleTask = useCallback(
		(task) => {
			setNoteValue(task?.note || '')
			setOpenTask((prev) => {
				return { ...prev, openTask: task }
			})
			dispatchAnalyticsEvent(
				'openTask',
				AmplitudeTaskParameters.fromJSON(task),
				user
			)
		},
		[dispatchAnalyticsEvent, setNoteValue, setOpenTask, user]
	)

	const closeTask = useCallback(
		(shouldDispatchEvent = true) => {
			if (shouldDispatchEvent) {
				dispatchAnalyticsEvent(
					'closeTask',
					AmplitudeTaskParameters.fromJSON(currentOpenedTask.openTask)
				)
			}

			setOpenTask({
				openTask: null,
			})
		},
		[currentOpenedTask?.openTask, dispatchAnalyticsEvent, setOpenTask]
	)

	return { openTask, openNextTask, openSingleTask, closeTask }
}

export const useInbox = () => {
	const setLastFetchInboxUpdate = useRecoilCallback(({ set }) => (date) => {
		set(lastFetchInboxUpdate, date)
	})

	const [visibleData, setVisibleData] = useState(null)

	const [expandedInbox, setExpandedInbox] = useRecoilState(expandedInboxState)
	const [forceInboxUpdate, setForceInboxUpdate] = useRecoilState(
		forceInboxUpdateState
	)
	const [unreadInbox, setUnreadInbox] = useState(null)
	const [inboxHasNewData, setInboxHasNewData] =
		useRecoilState(inboxHasNewDataState)
	const [inboxFilters, setInboxFilters] = useRecoilState(inboxFiltersState)
	const totalAppliedFilters =
		inboxFilters.branch?.length + inboxFilters.insuranceCompany?.length

	const inbox = useRecoilValueLoadable(fetchInbox)
	const inboxData = inbox?.state === 'hasValue' ? inbox.contents : visibleData

	useEffect(() => {
		if (inboxFilters) {
			let notifications =
				visibleData?.allNotifications ||
				visibleData?.notifications ||
				[]

			if (notifications.length) {
				if (inboxFilters.insuranceCompany?.length) {
					notifications = notifications.filter((elem) => {
						return inboxFilters.insuranceCompany.includes(
							elem.insuranceEntityId.toString()
						)
					})
				}

				if (inboxFilters.branch?.length) {
					notifications = notifications.filter((elem) => {
						return inboxFilters.branch.includes(elem.causaltyBranch)
					})
				}

				if (inboxFilters.sort) {
					if (inboxFilters.sort === 'asc') {
						notifications = [...notifications].sort((a, b) => {
							return a.date.timestamp - b.date.timestamp
						})
					} else {
						notifications = [...notifications].sort((a, b) => {
							return b.date.timestamp - a.date.timestamp
						})
					}
				}

				if (inboxFilters.date) {
					notifications = notifications.filter((ele) =>
						ele.date.momentDate.isSame(inboxFilters.date, 'day')
					)
				}

				setVisibleData((prev) => ({
					...prev,
					filteredNotifications: notifications,
				}))
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inboxFilters, visibleData?.notifications])

	useEffect(() => {
		if (!visibleData && inboxData) {
			setVisibleData((prev) => ({
				...prev,
				notifications: inboxData?.notifications,
			}))

			return
		}
	}, [inboxData, visibleData])

	useEffect(() => {
		if (forceInboxUpdate && inbox.state === 'hasValue') {
			const { notifications: inboxNotifications } = inboxData
			const notifications = inboxNotifications ?? []

			setVisibleData({
				notifications,
			})

			setForceInboxUpdate(false)
		}
	}, [
		forceInboxUpdate,
		inbox.state,
		inboxData,
		setForceInboxUpdate,
		setInboxFilters,
	])

	useDeepCompareEffect(() => {
		if (inboxData !== null && visibleData) {
			const { notifications: inboxNotifications } = inboxData
			const { notifications: inboxPrevNotifications } = visibleData

			const notifications = inboxNotifications ?? []
			const prevNotifications = inboxPrevNotifications ?? []

			let hasNewData = false

			if (!expandedInbox) {
				setVisibleData((prev) => ({ ...prev, notifications }))
			} else {
				setVisibleData((prev) => {
					const updatedPrevNotifications = (
						prev?.notifications || []
					).map((prevNotification) => {
						const newNotification = (notifications || []).find(
							(f) => f.id === prevNotification.id
						)

						if (!newNotification) {
							return {
								...prevNotification,
								closed: true,
								key: `${prevNotification.key}_closed`,
							}
						}

						return { ...newNotification }
					})

					return { ...prev, notifications: updatedPrevNotifications }
				})
				if (
					!forceInboxUpdate &&
					prevNotifications?.length !== notifications?.length
				) {
					hasNewData = true
				}

				if (!hasNewData) {
					const prevNotificationsIds = new Set(
						(prevNotifications || []).map((elem) => elem.id)
					)

					const isDifferent = notifications.some(
						(notification) =>
							!prevNotificationsIds.has(notification.id)
					)

					if (!forceInboxUpdate && isDifferent) {
						hasNewData = true
					}
				}
			}

			setInboxHasNewData(hasNewData)

			setUnreadInbox({
				payload: {
					notifications: inboxData?.notifications,
				},
			})
		}
	}, [inboxData?.notifications ?? {}])

	const reloadInbox = () => {
		setLastFetchInboxUpdate(Date.now())
	}

	return {
		reloadInbox,
		visibleData,
		unreadInbox,
		inboxHasNewData,
		setForceInboxUpdate,
		expandedInbox,
		setExpandedInbox,
		inboxFilters,
		setInboxFilters,
		totalAppliedFilters,
	}
}

/**
 * Hook for fetching data from external APIs outside the main API structure
 * @param {string} url - Full URL of the external API endpoint
 * @param {Object} options - Fetch options (method, headers, body)
 * @param {Array} dependencies - Array of dependencies to trigger refetch
 * @returns {Object} { data, loading, error }
 */
export const useExternalFetch = (URL, options = {}, dependencies = []) => {
	const [state, setState] = useState({
		data: null,
		loading: true,
		error: null,
	})

	useEffect(() => {
		if (!URL) return

		const fetchData = async () => {
			setState((prev) => ({ ...prev, loading: true }))

			try {
				const safeOptions = options ?? {}
				const response = await fetch(URL, {
					...safeOptions,
					headers: {
						'Content-Type': 'application/json',
						...safeOptions.headers,
					},
				})
				if (!response.ok) {
					throw new Error(
						`Error ${response.status}: ${response.statusText}`
					)
				}
				// Handle different response types
				let data
				const contentType = response.headers.get('content-type')
				if (contentType && contentType.includes('application/json')) {
					data = await response.json()
				} else {
					data = await response.text()
				}

				setState({
					data: data,
					loading: false,
					error: null,
				})
			} catch (error) {
				console.error('External fetch error:', error)
				setState({
					data: null,
					loading: false,
					error: error.message || 'Failed to fetch data',
				})
			}
		}

		fetchData()
	}, dependencies)

	return state
}

/**
 * Function for one-time external API requests
 * @param {string} url - Full URL of the external API endpoint
 * @param {Object} options - Fetch options (method, headers, body)
 * @returns {Promise} Promise that resolves with the response data
 */
export const externalFetchOnce = async (url, options = {}) => {
	try {
		const response = await fetch(url, {
			...options,
			headers: {
				'Content-Type': 'application/json',
				...options.headers,
			},
		})

		if (!response.ok) {
			throw new Error(`Error ${response.status}: ${response.statusText}`)
		}

		// Handle different response types
		const contentType = response.headers.get('content-type')
		if (contentType && contentType.includes('application/json')) {
			return await response.json()
		}
		return await response.text()
	} catch (error) {
		console.error('External fetch error:', error)
		throw error
	}
}
